import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Input } from '@/components/ui/input';
import { Mail, AlertTriangle, RefreshCw, ExternalLink, Loader2 } from 'lucide-react';
import { Capacitor } from '@capacitor/core';

interface EmailVerificationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  email: string;
  /**
   * Verifies the code from the email. Resolves with an error to display inline,
   * or no error on success (the caller navigates away, unmounting this modal).
   */
  onVerify: (code: string) => Promise<{ error: Error | null }>;
  /** Resends the code. Should resolve `true` only on success (starts the cooldown). */
  onResend?: () => Promise<boolean | void> | boolean | void;
}

// Minimum length we accept before enabling submit. Supabase email OTP is 6–10
// digits; we don't hard-code an exact length because it's a server setting, so a
// single flexible input fits any length (and enables OS one-time-code autofill).
const MIN_CODE_LEN = 6;

// The signup confirmation email carries a numeric code AND a link (hybrid
// template). Entering the code keeps the user in-app — solving the cross-device
// / deep-link fragility of the link-only flow. The link stays as a fallback.
export function EmailVerificationModal({ open, onOpenChange, email, onVerify, onResend }: EmailVerificationModalProps) {
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(0);
  const [isResending, setIsResending] = useState(false);

  // Reset transient state whenever the modal (re)opens — including the cooldown,
  // so a stale countdown from a previous open (or a different e-mail) never blocks
  // the resend button.
  useEffect(() => {
    if (open) {
      setCode('');
      setError(null);
      setVerifying(false);
      setCountdown(0);
      setIsResending(false);
    }
  }, [open]);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => setCountdown((prev) => prev - 1), 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  const submit = async () => {
    const value = code.trim();
    if (verifying || value.length < MIN_CODE_LEN) return;
    setVerifying(true);
    setError(null);
    const { error } = await onVerify(value);
    setVerifying(false);
    if (error) {
      // Supabase returns a combined "invalid or expired" message for both cases.
      setError('Código inválido ou expirado. Confira os dígitos ou reenvie.');
      setCode('');
    }
    // On success the caller navigates away and unmounts this modal.
  };

  // Keep only digits (Supabase email OTP is numeric); strip pasted spaces/dashes.
  const handleChange = (raw: string) => {
    setError(null);
    setCode(raw.replace(/\D/g, '').slice(0, 10));
  };

  const handleResend = async () => {
    if (countdown > 0 || isResending) return;
    setIsResending(true);
    setError(null);
    try {
      const ok = await onResend?.();
      // Only clear the code and start the cooldown on a real success — a failed /
      // rate-limited resend must not lock the button for 60s nor wipe what the
      // user already typed.
      if (ok) {
        setCode('');
        setCountdown(60);
      }
    } finally {
      setIsResending(false);
    }
  };

  // Opens the e-mail app's INBOX (not the compose/new-message screen). `mailto:`
  // always lands on compose, so we target the platform's inbox entry point:
  //  - Android: an intent to the MAIN/APP_EMAIL category (default mail app inbox,
  //    or a chooser of installed mail apps).
  //  - iOS: `message://` opens Apple Mail's inbox.
  // Fallback to `mailto:` if the inbox intent isn't handled by any app.
  const openEmailInbox = () => {
    const platform = Capacitor.getPlatform();
    try {
      if (platform === 'android') {
        window.location.href =
          'intent://#Intent;action=android.intent.action.MAIN;category=android.intent.category.APP_EMAIL;end';
        return;
      }
      if (platform === 'ios') {
        window.location.href = 'message://';
        return;
      }
    } catch {
      // Fall through to the mailto fallback below.
    }
    window.location.href = 'mailto:';
  };

  const formatCountdown = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md text-center">
        {/* Green envelope icon */}
        <div className="mx-auto mb-2 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
          <Mail className="h-8 w-8 text-green-600" />
        </div>

        <DialogTitle className="text-xl font-semibold text-foreground">Confirme seu e-mail</DialogTitle>

        <DialogDescription className="text-sm text-muted-foreground">
          Enviamos um código para <span className="font-medium text-primary">{email}</span>
        </DialogDescription>

        {/* Code input — single flexible field (fits any OTP length + autofill) */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
          className="mt-2 space-y-2"
        >
          <Input
            value={code}
            onChange={(e) => handleChange(e.target.value)}
            disabled={verifying}
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            placeholder="Digite o código"
            aria-label="Código de confirmação"
            className="text-center text-2xl font-semibold tracking-[0.4em] placeholder:text-base placeholder:font-normal placeholder:tracking-normal placeholder:text-muted-foreground/60"
          />

          {error && <p className="text-sm text-destructive">{error}</p>}

          <Button3D type="submit" className="w-full" disabled={verifying || code.trim().length < MIN_CODE_LEN}>
            {verifying ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Confirmando...
              </>
            ) : (
              'Confirmar'
            )}
          </Button3D>
        </form>

        {/* Hint: the email also carries a link, for anyone who prefers it. */}
        <p className="text-xs text-muted-foreground">
          Também enviamos um link no e-mail, caso prefira confirmar por lá.
        </p>

        {/* SPAM warning */}
        <div className="flex items-center justify-center gap-2 rounded-lg bg-yellow-50 p-3 text-sm text-yellow-800">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span>Não recebeu? Verifique também sua caixa de SPAM.</span>
        </div>

        {/* Open Email App button (native only) */}
        {Capacitor.isNativePlatform() && (
          <Button
            variant="outline"
            className="w-full"
            onClick={openEmailInbox}
          >
            <ExternalLink className="mr-2 h-4 w-4" />
            Abrir app de e-mail
          </Button>
        )}

        {/* Resend */}
        <Button
          variant="ghost"
          className="w-full"
          onClick={handleResend}
          disabled={countdown > 0 || isResending}
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${isResending ? 'animate-spin' : ''}`} />
          {isResending
            ? 'Reenviando...'
            : countdown > 0
              ? `Reenviar em ${formatCountdown(countdown)}`
              : 'Reenviar código'}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
